import { memo, type RefObject } from 'react';

import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { DataPointCanvas } from '../../../../core/components/data-point/DataPointCanvas.js';
import {
  DataPoint,
  GroupDataPoint,
} from '../../../../core/types/data-point.js';
import { datapointIndicatorLayout } from '../TimeseriesOverlays.sty.js';
import { TimeseriesClosestPoint } from '../utils/tooltip/find-closest-points.js';

interface TimeseriesChartDatapointsCanvasProps {
  /** Closest point to the mouse position & the x and y position of the tooltip */
  closestPoint?: TimeseriesClosestPoint;
  groupedPoints: GroupDataPoint;
}

/**
 * Component for rendering the datapoints on the Y axis based on an X coordinate.
 */
export const TimeseriesChartDatapoints = memo(
  (props: TimeseriesChartDatapointsCanvasProps) => {
    const { closestPoint, groupedPoints } = props;
    const { width, height } = useGridPlotSize();

    return (
      <div className={datapointIndicatorLayout}>
        <CanvasContext.Provider
          value={{
            shapeRefs: new Map<string, RefObject<CanvasShape>>(),
          }}
        >
          <Canvas
            width={width + OVERLAY_PADDING * 2}
            height={height + OVERLAY_PADDING * 2}
          >
            {groupedPoints.length > 0 &&
              groupedPoints.map((pointGroup, index) => (
                <DataPointCanvas
                  key={`pointGroup-${index}`}
                  points={pointGroup}
                  closestPoint={closestPoint as DataPoint}
                />
              ))}
          </Canvas>
        </CanvasContext.Provider>
      </div>
    );
  },
);

TimeseriesChartDatapoints['displayName'] = 'TimeseriesChartDatapoints';
